import { type ReactNode, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { type AddToCartItem, sendAddToCart } from "~/feature/cart/cart/api/AddToCart";
import { Card } from "~/Configurators/Linear/components/Card";
import type { ContractDetailsResponse } from "~/Contracts/models";
import type { AvailabilityInfo } from "~/api/pna/AvailabilityInfo";
import { Button } from "~/common/components/Button/Button";
import { Link, LinkType } from "~/common/components/Link";
import { isPackageRestrictionEnabled } from "~/common/components/PackageRestrictionContext/utils/isPackageRestrictionEnabled";
import { Stacker } from "~/common/components/Stacker/Stacker";
import { Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { ImageFormat } from "~/dto/ProductDataDTO";
import { DateFormatType, formatDate } from "~/utils/DateFormatter";
import { fakeDescriptorFromProduct, isProductPMA } from "~/feature/ProductDescriptor/v1/productDescriptorUtils";
import type { PopupData } from "../AddToCartPopup/ContractVersionContents";
import NewAddToCartPopup from "../AddToCartPopup/AddToCartPopup";
import classes from "./ContractDetailsSummary.module.less";
import { openModal } from "~/AddToCart/ModalSevice";
import CartSuccessModal from "~/AddToCart/components/AddToCartProcess/Modals/CartSuccessModal/CartSuccessModal";
import { ContractAddToCartPopupStates } from "~/Contracts/components/AddToCartPopup/ContractAddToCartPopupStates";
import { Alert } from "~/common/m3/Alert/Alert";
import { AnalyticsMQ } from "~/feature/Analytics/AnalyticsMQ/AnalyticsMQ";

interface NewContractDetailsSummaryProps {
  contractDetails: ContractDetailsResponse;
  productAvailability?: AvailabilityInfo;
  className?: string;
}

const transformContractDetailsToATCItem = (
  contractDetails: ContractDetailsResponse,
  popupData: PopupData,
  productAvailability: AvailabilityInfo | undefined,
): AddToCartItem => {
  const { quantity, requestedDeliveryDate } = popupData;

  const price = contractDetails.nettoPrice;
  const packageCode = contractDetails.packCode;
  const packageSize = contractDetails.packSize;

  const packagingInfo =
    productAvailability?.packagingInformation &&
    productAvailability.packagingInformation.filter((item) => item.packCode === packageCode.toString())[0];

  const primaryWarehouseCode = packagingInfo && packagingInfo.primaryWarehouseCode;

  const netPrice = price.value;

  return {
    productCode: contractDetails.product.code,
    packageSize: packageSize,
    packageCode: packageCode.toString(),
    quantity: quantity,
    comment: "",
    netPrice: netPrice,
    priceUnit: 1, // TODO: Change me if contracts provide price unit
    selectedDeliveryDate: requestedDeliveryDate,
    selectedDeliveryDateIsManual: true,
    primaryWarehouseCode: primaryWarehouseCode ?? "",
    contractLine: contractDetails.lineNumber.toString() ?? "",
    contractNumber: contractDetails.schaefflerContractNumber,
    fifteenDigit: false,
    // Contracts are a fixed price. No price reference here
    priceReference: "",
    isPMA: isProductPMA(fakeDescriptorFromProduct(contractDetails.product)),

    restrictedPackage: isPackageRestrictionEnabled(),
    // There is no surcharge in contracts
    surcharge: 0,
    originCountry: "", // TODO: Provide origin country for contracts
    contractOpenQuantity: contractDetails.openQuantity,

    tariffSurchargeApplicable: false,
  };
};

// copied from contract actions, refactored to not use redux
//TODO: investigate how both functions can be brought back together
const contractItemAddToCart = (
  contract: ContractDetailsResponse,
  data: PopupData,
  availability?: AvailabilityInfo,
  onSuccess?: () => void,
  onError?: () => void,
) => {
  const contractItem = transformContractDetailsToATCItem(contract, data, availability);

  if (!contractItem) {
    return undefined;
  }

  AnalyticsMQ.instance.push("addToCart", {
    products: [
      {
        product: contract.product,
        quantity: data.quantity,
        price: {
          ...contract.nettoPrice,
          unit: +contract.priceUnit,
          baseValue: contract.nettoPrice.value,
          surcharge: 0,
        },
        packaging: {
          code: contract.packCode.toString(),
          size: contract.packSize,
        },
      },
    ],
    context: {
      source: "unknown",
    },
  });

  sendAddToCart(contractItem)
    .then((response) => {
      if (response.ok) {
        onSuccess?.();
      } else {
        onError?.();
      }
    })
    .catch((err) => {
      console.error(err);
      onError?.();
    });
};

const Tile = ({ title, children }: { title: string; children?: ReactNode }) => {
  return (
    <Card className={classes.tile}>
      <Stacker gap={16}>
        <div className={classes.tileTitle}>{title}</div>
        <div>{children}</div>
      </Stacker>
    </Card>
  );
};

const InfoEntry = ({ label, value }: { label: string; value: ReactNode }) => {
  return (
    <Stacker gap={4}>
      <div className={classes.infoLabel}>{label}</div>
      <div>{value}</div>
    </Stacker>
  );
};

const NetSumTile = ({
  nextDisposition,
  quantity,
  netPrice,
  totalPrice,
  contractDetails,
  predecessor,
  productAvailability,
}: {
  nextDisposition: string;
  quantity: string;
  netPrice: ContractDetailsResponse["nettoPrice"];
  totalPrice: ContractDetailsResponse["totalPrice"];
  contractDetails: ContractDetailsResponse;
  predecessor: { detailsUrl?: string; contractNumber?: string };
  productAvailability?: AvailabilityInfo;
}) => {
  const [contractStatus, setContractStatus] = useState<ContractAddToCartPopupStates>(
    ContractAddToCartPopupStates.LOADED,
  );
  const { val: popup, setTrue: open, setFalse: close } = useBooleanState(false);
  const hasPredecessor = !!predecessor.contractNumber;
  const { t } = useTranslation();

  return (
    <div className={classes.netSumTile}>
      <Stacker gap={8} className={classes.netSumInfo}>
        <div className="d-flex flex-justify-between">
          <div>{t("contractOverview.table.disposition")}</div>
          <div>{nextDisposition}</div>
        </div>
        <div className="d-flex flex-justify-between">
          <div>{t("contractDetails.nextDispositionQuantity")}</div>
          <div>{quantity}</div>
        </div>
        <div className="d-flex flex-justify-between">
          <div>{t("common.product.netPricePerPiece:::one")}</div>
          <div>{netPrice.formattedValue}</div>
        </div>
        <div className={classes.netSumTotal}>
          <div className={classes.netSumValue}>
            <p>{t("wishlistPage.total")}</p>
            <p className={classes.infoLabel}>{t("cartPage.taxes")}</p>
          </div>
          <div>{totalPrice.formattedValue}</div>
        </div>
      </Stacker>
      <Stacker gap={16}>
        <Button
          onClick={open}
          startIcon={<Icon type={IconType.Cart} />}
          disabled={hasPredecessor}
          clta-details-page-element-marker={"add-to-cart"}
          clta-product-name={contractDetails.product.designation}
          clta-product-id={contractDetails.product.sapMaterialNumber}
          clta-total-value={totalPrice.value.toString()}
        >
          {t("recentlyViewProducts.addProducts")}
        </Button>
        {hasPredecessor && (
          <Alert severity="info">
            <p>{t("contractDetails.basedOnPrevious")}</p>
            <Trans i18nKey="contractDetails.pleaseComplete" values={{ contractNumber: predecessor.contractNumber }}>
              Please complete
              <Link className="f-bold" href={predecessor.detailsUrl} variant={LinkType.NAVIGATION}>
                contract {predecessor.contractNumber}
              </Link>
              first.
            </Trans>
          </Alert>
        )}
      </Stacker>

      {popup && (
        <NewAddToCartPopup
          contractDetails={contractDetails}
          contractStatus={contractStatus}
          product={contractDetails.product}
          onClose={() => {
            close();
            setContractStatus(ContractAddToCartPopupStates.LOADED);
          }}
          onAdd={(e) => {
            setContractStatus(ContractAddToCartPopupStates.LOADING);
            contractItemAddToCart(
              contractDetails,
              e,
              productAvailability,
              () => {
                close();
                setContractStatus(ContractAddToCartPopupStates.LOADED);
                openModal("add-to-cart-dialog", (close) => (
                  <CartSuccessModal
                    formData={[transformContractDetailsToATCItem(contractDetails, e, productAvailability)]}
                    onClose={close}
                    page="CONTRACT_DETAILS"
                  />
                ));
              },
              () => {
                setContractStatus(ContractAddToCartPopupStates.SEND_FAILURE);
              },
            );
          }}
        />
      )}
    </div>
  );
};

const NewContractDetailsSummary = ({
  contractDetails,
  productAvailability,
  className,
}: NewContractDetailsSummaryProps) => {
  const { t } = useTranslation();
  const nextDisposition =
    contractDetails.nextDisposition && formatDate(contractDetails.nextDisposition, DateFormatType.DATE);
  const contractPeriod =
    contractDetails.contractValidFrom &&
    contractDetails.contractValidTo &&
    `${formatDate(contractDetails.contractValidFrom, DateFormatType.DATE)} - ${formatDate(
      contractDetails.contractValidTo,
      DateFormatType.DATE,
    )}`;
  const datePlaced = contractDetails.datePlaced && formatDate(contractDetails.datePlaced, DateFormatType.DATE);
  const { product, shipToAddress } = contractDetails;
  return (
    <div className={classes.root}>
      <Tile title={t("newContract.contractTile")}>
        <Stacker gap={16}>
          <InfoEntry label={t("contractOverview.table.contractNo")} value={contractDetails.customerContractNumber} />
          <InfoEntry
            label={t("contractDetailsPage.summary.schaContractNo")}
            value={contractDetails.schaefflerContractNumber}
          />
          <div className={classes.subGrid}>
            <InfoEntry label={t("contractOverview.table.period")} value={contractPeriod ?? "-"} />
            <InfoEntry label={t("orderDetailsPage.summary.date")} value={datePlaced ?? "-"} />
          </div>
          <InfoEntry
            label={t("contractOverview.table.netPrice")}
            value={<span className="f-bold">{contractDetails.totalPrice.formattedValue}</span>}
          />
          <div className={classes.subGrid}>
            <InfoEntry label={t("contractDetails.totals.totalQty")} value={contractDetails.quantityRequested} />
            <InfoEntry label={t("contractDetails.openQuantity")} value={contractDetails.openQuantity} />
          </div>
        </Stacker>
      </Tile>
      <Tile title={t("feedback.option.invoiceProduct")}>
        <Stacker gap={16}>
          <Stacker gap={16} direction="row">
            <Link href={product.url}>
              <img width={64} src={product.images?.find((image) => image.format === ImageFormat.thumbnail)?.url} />
            </Link>

            <Link variant={LinkType.NAVIGATION} href={product.url} className={classes.alignStart}>
              <span className="f-bold">{product.designation}</span>
            </Link>
          </Stacker>
          <InfoEntry
            label={t("addToCart.contract.packaging")}
            value={t("addToCartPopup.packSize", { amount: contractDetails.packSize, code: contractDetails.packCode })}
          />
          <InfoEntry
            label={t("productComparison.download.schaefflerId")}
            value={contractDetails.product.sapMaterialNumber}
          />
          <InfoEntry
            label={t("orderListPage.queryType.customMaterialNumber")}
            value={contractDetails.customerMaterial}
          />
        </Stacker>
      </Tile>
      <Tile title={t("newContract.shippingTile")}>
        <Stacker gap={16}>
          <InfoEntry
            label={t("checkoutPage.headers.shippingAddress")}
            value={
              <Stacker gap={0}>
                <span className="f-bold">{shipToAddress?.companyName}</span>
                <span>{`${shipToAddress?.lines?.map((line) => `${line} `)}`}</span>
                <span>{`${shipToAddress?.postalCode ?? ""} ${shipToAddress?.town ?? ""}`}</span>
                <span>{t(shipToAddress?.country.name)}</span>
              </Stacker>
            }
          />
          <InfoEntry label={t("cartPage.shipmentDetails.freightPayment")} value={t("contractDetailsPage.freight")} />
        </Stacker>
      </Tile>
      <NetSumTile
        nextDisposition={nextDisposition ?? "-"}
        quantity={contractDetails.nextDispositionQuantity}
        netPrice={contractDetails.nettoPrice}
        totalPrice={contractDetails.totalPrice}
        contractDetails={contractDetails}
        productAvailability={productAvailability}
        predecessor={contractDetails.contractPredecessor}
      />
    </div>
  );
};

export default NewContractDetailsSummary;
